import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, X, Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  useServiceStaticFiles,
  useUploadServiceStaticFile,
  useDeleteServiceStaticFile,
  ADVANCED_TYPE_HONEYPOT,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './HoneypotForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = ADVANCED_TYPE_HONEYPOT;
/** The static-file `type` scope for honeypot HTML pages. */
const FILE_TYPE = 'honeypot_protection';

/** Action policies. "Return HTML" (ResponseHtml) is confirmed; the By-pass value
 *  is best-effort and may need adjusting against the portal. */
const ACTIONS = [
  { value: 'ResponseHtml', label: 'Return HTML' },
  { value: 'Bypass', label: 'By-pass' },
] as const;

/** Common bait paths pre-filled for a new honeypot rule. */
const DEFAULT_PATHS = [
  '/login',
  '/admin_login',
  '/admin/login',
  '/login.html',
  '/login.php',
  '/admin_login.php',
  '/phpinfo.php',
  '/config/config.inc.php.bak',
];

/** The Honeypot Protection (type 9) per-type config. */
interface HoneypotConfig {
  path_list: string[];
  action: string;
  /** The selected uploaded HTML file name (for Return HTML). */
  redirect_value: string;
}

interface FormState {
  name: string;
  description: string;
  sites: number[];
  path_list: string[];
  action: string;
  redirect_value: string;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  path_list: [...DEFAULT_PATHS],
  action: 'ResponseHtml',
  redirect_value: '',
});

/**
 * Add / Edit a Honeypot Protection rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 9). Bait paths (path_list) trap scanners; on a hit the
 * Return-HTML action serves an uploaded static page (redirect_value). Builds the
 * per-type `config`; the BE adds the common envelope keys. Routes:
 * aiwaf/advanced/honeypot/new + /:id.
 */
export function HoneypotForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<HoneypotConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);
  const { data: files = [] } = useServiceStaticFiles(serviceId, FILE_TYPE);
  // The vendor stores redirect_value by file NAME, so duplicate-named uploads are
  // interchangeable — dedupe by name (a Select can't have two items share a value).
  const fileNames = useMemo(() => Array.from(new Set(files.map((f) => f.name))), [files]);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Options for the "Select a File" dropdown: the account's uploaded files PLUS
  // the rule's current redirect_value. The vendor references files by name, so a
  // saved rule can point at a file that is no longer in the list (deleted, or a
  // different session) — without folding it in, the Select would drop the current
  // selection and render blank. Mirrors the vendor portal, which shows the saved
  // file name even when the upload list is empty.
  const fileOptions = useMemo(() => {
    const names = new Set(fileNames);
    if (form.redirect_value) names.add(form.redirect_value);
    return Array.from(names);
  }, [fileNames, form.redirect_value]);

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as HoneypotConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      path_list: Array.isArray(c.path_list) ? c.path_list : [],
      action: c.action || 'ResponseHtml',
      redirect_value: c.redirect_value ?? '',
    });
  }, [existing]);

  // Honeypot-path chip input.
  const [pathDraft, setPathDraft] = useState('');
  const addPath = () => {
    const v = pathDraft.trim();
    if (!v || form.path_list.includes(v)) {
      setPathDraft('');
      return;
    }
    set('path_list', [...form.path_list, v]);
    setPathDraft('');
  };
  const removePath = (p: string) => set('path_list', form.path_list.filter((x) => x !== p));

  // File upload (with replace-on-duplicate-name).
  const fileInput = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ content: string; name: string } | null>(null);
  const upload = useUploadServiceStaticFile(serviceId ?? '', {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: `Uploaded ${vars.name}` });
      set('redirect_value', vars.name); // auto-select the just-uploaded file
    },
    onError: (e) => show({ tone: 'danger', title: 'Upload failed', description: errMessage(e) }),
  });
  const del = useDeleteServiceStaticFile(serviceId ?? '');

  const doUpload = (p: { content: string; name: string }) =>
    upload.mutate({ content: p.content, name: p.name, type: FILE_TYPE });

  const onPickFile = async (file: File) => {
    const content = await file.text();
    const p = { content, name: file.name };
    // Same name already uploaded → ask before replacing (the vendor appends, so a
    // bare re-upload would create a duplicate).
    if (files.some((f) => f.name === file.name)) setPending(p);
    else doUpload(p);
  };

  const confirmReplace = async () => {
    if (!pending) return;
    try {
      const dupes = files.filter((f) => f.name === pending.name);
      await Promise.all(dupes.map((f) => del.mutateAsync(f.id)));
      doUpload(pending);
    } catch (e) {
      show({ tone: 'danger', title: 'Replace failed', description: errMessage(e) });
    } finally {
      setPending(null);
    }
  };

  // Apply-to.
  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<HoneypotConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'honeypot_rule', 'create');
      show({ tone: 'success', title: 'Honeypot Protection rule created' });
      navigate(`${base}/aiwaf/advanced?tab=honeypot`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<HoneypotConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'honeypot_rule', 'edit');
      show({ tone: 'success', title: 'Honeypot Protection rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=honeypot`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const fileValid = form.action !== 'ResponseHtml' ? true : !!form.redirect_value;
  const canSubmit = nameValid && form.path_list.length > 0 && fileValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<HoneypotConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        path_list: form.path_list,
        action: form.action,
        redirect_value: form.action === 'ResponseHtml' ? form.redirect_value : '',
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit Honeypot Rule' : 'Honeypot Protection Rule' },
            ]}
          />
        }
        title="Honeypot Protection Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
            </CardHeader>
            <CardBody>
            <Field
              label="Honeypot path"
              required
              description="Paths often seen on websites, used as bait against attackers. Type a path and press Enter to add."
            >
              {() => (
                <div className={styles.chipBox}>
                  {form.path_list.map((p) => (
                    <span key={p} className={styles.chip}>
                      {p}
                      <X
                        size={13}
                        className={styles.removeIcon}
                        onClick={() => removePath(p)}
                        aria-label={`Remove ${p}`}
                      />
                    </span>
                  ))}
                  <input
                    value={pathDraft}
                    maxLength={512}
                    placeholder={form.path_list.length ? '' : '/login'}
                    onChange={(e) => setPathDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPath();
                      } else if (e.key === 'Backspace' && !pathDraft && form.path_list.length) {
                        removePath(form.path_list[form.path_list.length - 1]);
                      }
                    }}
                    onBlur={addPath}
                    className={styles.chipInput}
                  />
                </div>
              )}
            </Field>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Action</CardTitle>
              <CardDescription>Return HTML serves an uploaded webpage; By-pass lets the request through.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              {form.action === 'ResponseHtml' && (
                <div className={styles.uploadBox}>
                  <div>
                    <div className={styles.bold}>Upload File</div>
                    <div className={styles.uploadDesc}>
                      The file name can contain 1-64 characters: A-Z, a-z, 0-9, _ and . (dot).
                    </div>
                    <input
                      ref={fileInput}
                      type="file"
                      accept=".html,.htm,text/html"
                      className={styles.hiddenInput}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onPickFile(f);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="secondary"
                      startIcon={<Upload size={14} />}
                      disabled={upload.isPending}
                      onClick={() => fileInput.current?.click()}
                    >
                      {upload.isPending ? 'Uploading…' : 'Upload'}
                    </Button>
                  </div>

                  <Field label="Select a File" required>
                    {() => (
                      <Select
                        value={form.redirect_value || undefined}
                        onValueChange={(v) => set('redirect_value', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a File" />
                        </SelectTrigger>
                        <SelectContent>
                          {fileOptions.length === 0 ? (
                            <SelectItem value="__none" disabled>
                              No files uploaded yet
                            </SelectItem>
                          ) : (
                            fileOptions.map((name) => (
                              <SelectItem key={name} value={name}>
                                {name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                </div>
              )}
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=honeypot`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>

          <ConfirmDialog
            open={!!pending}
            onOpenChange={(o) => !o && setPending(null)}
            title="Replace existing file?"
            description={`A file named "${pending?.name}" already exists. Replace it with the new upload?`}
            confirmLabel="Replace"
            loading={del.isPending || upload.isPending}
            onConfirm={confirmReplace}
          />
        </div>
    </Container>
  );
}
